/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { postLogin } from './thunk';
import { LoginResponse, Token, User } from './types';
import { setAuthHintCookie } from '@/utils/auth';

interface LoginState {
  user: User | null;
  token: Token | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: LoginState = {
  user: null,
  token: null,
  status: 'idle',
  error: null,
};

const loginSlice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    logout(state) {
      state.user = null;
      state.token = null;
      state.status = 'idle';
      state.error = null;
      // (optional) clear storage here if you want:
      if (typeof window !== 'undefined') {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('username');
        setAuthHintCookie(false); // 👈 add this
      }
    },
    setTokenFromStorage(state, action: PayloadAction<Token | null>) {
      state.token = action.payload;
    },
    setUser(state, action: PayloadAction<User | null>) {
      state.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(postLogin.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(
        postLogin.fulfilled,
        (state, action: PayloadAction<LoginResponse>) => {
          state.status = 'succeeded';
          state.user = action.payload.user;
          state.token = action.payload.token;

          if (typeof window !== 'undefined') {
            localStorage.setItem('accessToken', action.payload.token.access);
            localStorage.setItem('refreshToken', action.payload.token.refresh);
            const displayName =
              action.payload.user.first_name ||
              action.payload.user.username ||
              'User';
            localStorage.setItem('username', displayName);

            setAuthHintCookie(true); // 👈 add this
          }
        },
      )
      .addCase(postLogin.rejected, (state, action: any) => {
        state.status = 'failed';
        state.error =
          action?.payload ?? action?.error?.message ?? 'Unknown error';
      });
  },
});

export const { logout, setTokenFromStorage, setUser } = loginSlice.actions;
export default loginSlice.reducer;
